import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { WorkspaceRole } from '@/types';
import { useAuth } from '@/providers/AuthProvider';
import { resourcesApi, ApiError } from '@/services/api';

type BootstrapState = 'loading' | 'ready' | 'no-access';

interface User {
  id: string;
  name: string;
  email: string;
  workspaceRole: WorkspaceRole;
  timezoneOverride?: string | null; // User-specific timezone preference
}

interface UserContextType {
  user: User;
  setUser: (user: User) => void;
  updateUserTimezone: (timezone: string | null) => Promise<void>;
  bootstrapState: BootstrapState;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

// Mock user - in a real app, this would come from authentication
const defaultUser: User = {
  id: 'user-1',
  name: 'John Doe',
  email: 'john.doe@example.com',
  workspaceRole: 'Administrator', // Default to Administrator for demo purposes
};

export function UserProvider({ children }: { children: ReactNode }) {
  const { userProfile, isProduction } = useAuth();
  const [user, setUser] = useState<User>(defaultUser);
  const [bootstrapState, setBootstrapState] = useState<BootstrapState>(
    isProduction ? 'loading' : 'ready'
  );

  // In live/production mode, populate user from the authenticated OIDC profile
  useEffect(() => {
    if (userProfile && userProfile.id) {
      setUser(prev => ({
        ...prev,
        id: userProfile.id,
        name: userProfile.name || prev.name,
        email: userProfile.email || prev.email,
      }));
    }
  }, [userProfile?.id, userProfile?.name, userProfile?.email]);

  // Fetch the current user's access from the backend.
  // Uses /resources/me for access control (resource found = has access, 404 = no access).
  // Workspace role comes from resource.applicationRole (set by Evo platform webhook).
  const fetchUserRole = useCallback(async () => {
    if (!isProduction) {
      setBootstrapState('ready');
      return;
    }

    try {
      const resource = await resourcesApi.me();

      // Block access if applicationRole is null/missing or explicitly "No Access"
      if (!resource.applicationRole || resource.applicationRole === 'No Access') {
        setBootstrapState('no-access');
        return;
      }

      const role: WorkspaceRole = resource.applicationRole === 'Administrator' ? 'Administrator' : 'User';
      setUser(prev => ({
        ...prev,
        id: String(resource.id),
        name: resource.name || prev.name,
        email: resource.email || prev.email,
        workspaceRole: role,
      }));
      setBootstrapState('ready');
    } catch (error) {
      if (error instanceof ApiError && error.statusCode === 404) {
        setBootstrapState('no-access');
      } else {
        console.warn('[UserContext] Failed to fetch resource, falling back to default', error);
        setBootstrapState('ready');
      }
    }
  }, [isProduction]);

  useEffect(() => {
    fetchUserRole();
  }, [fetchUserRole]);

  const updateUserTimezone = async (timezone: string | null) => {
    setUser({ ...user, timezoneOverride: timezone });
    
    // In a real app, this would persist to backend
    // For now, store in localStorage for persistence across sessions
    if (timezone) {
      localStorage.setItem(`user-timezone-${user.id}`, timezone);
    } else {
      localStorage.removeItem(`user-timezone-${user.id}`);
    }
  };

  // Load user timezone from localStorage on mount
  React.useEffect(() => {
    const savedTimezone = localStorage.getItem(`user-timezone-${user.id}`);
    if (savedTimezone) {
      setUser(prev => ({ ...prev, timezoneOverride: savedTimezone }));
    }
  }, [user.id]);

  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        updateUserTimezone,
        bootstrapState,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}
